"use client"
import { Box, Button, IconButton, Modal, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import "./QuarterlyReportsModal.css"
import { useEffect, useState } from "react"
import { getProjectUploads, getProjectQuarterHistory } from "../../Services/api"
import { exportToExcel } from "../../Utils/excelUtils"

export interface QuarterlyReport {
  year: number
  quarter: number
  start_date: string
  end_date: string
}

export interface ProjectQuarterData {
  project_id: string | number
  project: string;
  serial: string,
  year: number
  quarter: number
  company: string
  stage: string
  status: string,
  section_name: string
  subsection_name: string
  original_start_date: string
  updated_start_date: string
  updated_end_date: string
  previous_updated_start_date: string
  previous_updated_end_date: string
  end_date_change_months: number
  start_date_change_months: number
  current_quarter: string
  previous_quarter: string
  previous_status: string
  original_end_date: string
}

export type ReportsByYear = {
  [year: number]: {
    [quarter: number]: QuarterlyReport
  }
}

// שינוי: הוספת פרופ type שמקבל "stage" או "section"
interface QuarterlyReportsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string | number
  serialNumber: string | number
  type: "stage" | "section" // פרופ חדש: קובע את סוג התוכן
}

export default function QuarterlyReportsModal({
  open,
  onOpenChange,
  projectId,
  serialNumber,
  type // ✅ שימוש בפרופ החדש
}: QuarterlyReportsModalProps) {
  const [reports, setReports] = useState<QuarterlyReport[]>([])
  const [loading, setLoading] = useState(false)
  const [stageName, setStageName] = useState("")
  const [sectionName, setSectionName] = useState("")
  const [subsectionName, setSubsectionName] = useState("")

  const handleExport = async () => {
    try {
      const data = await getProjectQuarterHistory(projectId, serialNumber)

      const enrichedData: Record<string, any>[] = data.history.map((entry: any) => {
        // Create data structure in the correct order
        const result: Record<string, any> = {}
        
        // Base columns that come first for both types
        result.project = data.project || ''
        result.company = data.company || ''
        result.stage = data.stage_tag || ''
        result.stage_desc = data.stage_desc || ''
        
        // Add section properties only when type is "section" - insert them here
        if (type === "section") {
          result.section_name = data.section_tag || ''
          result.section_desc = data.section_desc || ''
          result.subsection_name = data.subsection_tag || ''
          result.subsection_desc = data.subsection_desc || ''
        }
        
        // Continue with remaining columns for both types
        result.quarter = entry.name || ''
        result.status = entry.status || ''
        result.original_start_date = entry.original_start_date || ''
        result.original_end_date = entry.original_end_date || ''
        result.updated_start_date = entry.updated_start_date || ''
        result.updated_end_date = entry.updated_end_date || ''

        return result
      })

      // Create column headers in the correct order based on type
      const columnHeaders: Record<string, string> = {}
      
      // Base columns that come first for both types
      columnHeaders.project = 'מיזם'
      columnHeaders.company = 'גוף מדווח'
      columnHeaders.stage = 'תווית שלב'
      columnHeaders.stage_desc = 'תיאור שלב'
      
      // Add section headers only if type is "section" - insert them here
      if (type === "section") {
        columnHeaders.section_name = 'תווית מקטע'
        columnHeaders.section_desc = 'תיאור מקטע'
        columnHeaders.subsection_name = 'תווית תת מקטע'
        columnHeaders.subsection_desc = 'תיאור תת מקטע'
      }
      
      // Continue with remaining columns for both types
      columnHeaders.quarter = 'רבעון'
      columnHeaders.status = 'סטטוס'
      columnHeaders.original_start_date = 'תחילת מיזם (מקורי)'
      columnHeaders.original_end_date = 'סיום מיזם (מקורי)'
      columnHeaders.updated_start_date = 'תחילת מיזם (מעודכן)'
      columnHeaders.updated_end_date = 'סיום מיזם (מעודכן)'

      exportToExcel(enrichedData, {
        fileName: 'QuarterlyHistoryReport.xlsx',
        sheetName: 'היסטוריית רבעונים',
        columnHeaders,
        highlightColumns: [],
      })
    } catch (error) {
      console.error("שגיאה בהורדת דוח אקסל:", error)
    }
  }

  useEffect(() => {
    if (open && projectId) {
      setLoading(true)
      getProjectUploads(projectId, serialNumber)
        .then((res) => {
          setReports(res?.data || [])

          // ✅ שינוי: תנאי לפי type - האם להציג שלב בלבד או גם מקטע ותת מקטע
          if (type === "stage") {
            setStageName(res.stage_name)
            setSectionName("")
            setSubsectionName("")
          } else {
            setStageName(res.stage_name)
            setSectionName(res.section_name)
            setSubsectionName(res.subsection_name)
          }
        })
        .catch((err) => {
          console.error("שגיאה בטעינת דוחות רבעוניים:", err)
          setReports([])
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [open, projectId, serialNumber, type])

  const organizeReportsByYear = (reports: QuarterlyReport[]): ReportsByYear => {
    const reportsByYear: ReportsByYear = {}
    reports.forEach((report) => {
      const year = Number(report.year)
      const quarter = Number(report.quarter)

      if (!Number.isInteger(year) || !Number.isInteger(quarter)) return

      if (!reportsByYear[year]) {
        reportsByYear[year] = {}
      }
      reportsByYear[year][quarter] = report
    })
    return reportsByYear
  }

  const reportsByYear = organizeReportsByYear(reports)
  const years = Array.from(new Set(reports.map((r) => r.year))).sort((a, b) => b - a)
  const quarters = [1, 2, 3, 4]

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()}`
  }

  return (
    <Modal role={'dialog'} aria-labelledby="dialogTitle" open={open} onClose={() => onOpenChange(false)}>
      <Box className="qr-modal">
        <Box className="qr-header">
          <Typography id={'dialogTitle'} variant="h2" className="qr-title">
            מערך שינויים עבור&nbsp;
            <span style={{ color: '#1293FE', fontWeight: 500 }}>{stageName}</span>
            {type === "section" && sectionName && (
              <>
                <img src="/images/arrow-left.png" alt="" className="qr-arrow" />
                <span style={{ color: '#1293FE', fontWeight: 500 }}>{sectionName}</span>
                {subsectionName && (
                  <>
                    <img src="/images/arrow-left.png" alt="" className="qr-arrow" />
                    <span style={{ color: '#1293FE', fontWeight: 500 }}>{subsectionName}</span>
                  </>
                )}
              </>
            )}
          </Typography>
          <IconButton aria-label="סגירת החלונית" onClick={() => onOpenChange(false)} className="qr-close-btn" size="small">
            <CloseIcon fontSize="small" sx={{ color: '#0C3768' }} />
          </IconButton>
        </Box>

        <Box className="qr-content">
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "40px" }}>
              <Typography>טוען נתונים...</Typography>
            </Box>
          ) : (
            <div className="qr-table-wrapper">
              <div className="qr-table-container">
                <table className="qr-table">
                  <thead>
                    <tr className="qr-quarters-header">
                      <th></th>
                      <th>Quarter 1</th>
                      <th>Quarter 2</th>
                      <th>Quarter 3</th>
                      <th>Quarter 4</th>
                    </tr>
                  </thead>
                  <tbody>
                    {years.map((year) => (
                      <tr key={year}>
                        <td className="qr-year">{year}</td>
                        {quarters.map((q) => {
                          const report = reportsByYear[year]?.[q]
                          return (
                            <td key={q} className="qr-data-cell">
                              {report ? (
                                <div className="qr-cell">
                                  <div className="qr-entry">
                                    <span>תאריך סיום עדכני: {formatDate(report.end_date)}</span>
                                  </div>
                                  <div className="qr-entry">
                                    <span>תאריך התחלה עדכני: {formatDate(report.start_date)}</span>
                                  </div>
                                </div>
                              ) : (
                                <span className="qr-empty">אין מידע</span>
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Box>

        <Box className="qr-footer">
          <Button
            variant="contained"
            color="primary"
            className="qr-download-btn"
            startIcon={<img src="/images/download-icon-white.png" alt="" className="qr-footer-icon" />}
            onClick={handleExport}
          >
            דו"ח השוואה של כל הדו"חות הרבעונים
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}
